import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Link2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';

import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { SlideKindSchema } from '@duke-hq/svc-api-db/schemas/enums/SlideKind.schema';

import {
  RequirementPicker,
  type RequirementPickerGroup,
} from '../../components/requirements/RequirementPicker';
import { RequirementChip } from '../../components/requirements/RequirementChip';
import { FoundationSkillPicker } from '../../components/requirements/FoundationSkillPicker';
import { trpcAdmin, type RouterOutputs } from '../../lib/trpcAdmin';
import { unitAssessmentState } from '../UnitAssessmentState';
import {
  UNIT_REQUIREMENT_KIND_COLOURS,
  unitRequirementLabel,
  UnitRequirementKindHeading,
} from '../../components/requirements/kinds';
import { testIds } from './testIds';

type UnitRequirement = RouterOutputs['unitAssessment']['listUnitRequirements'][number];

type SlideSelection = {
  editorMode: EditorMode | '';
  currentSlide: { id: string; kind: string } | null;
};

function linkableSlideId({ editorMode, currentSlide }: SlideSelection): string | undefined {
  if (editorMode !== EditorMode.EDIT || currentSlide == null) {
    return undefined;
  }

  return currentSlide.kind === SlideKindSchema.enum.CONTENT ? undefined : currentSlide.id;
}

export function LinkedRequirementsSection() {
  const { unitId } = useParams();
  const unitAssessmentSnap = useSnapshot(unitAssessmentState);
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const slideId = linkableSlideId(unitAssessmentSnap);
  const versionId = unitAssessmentSnap.draftVersionId;

  const { data: requirements = [] } = trpcAdmin.unitAssessment.listUnitRequirements.useQuery(
    { unitId: unitId ?? '' },
    { enabled: unitId != null },
  );
  const { data: linksBySlideId } = trpcAdmin.unitAssessment.getSlideRequirementLinks.useQuery(
    { unitAssessmentVersionId: versionId ?? '' },
    { enabled: versionId != null && requirements.length > 0 },
  );

  const mutationOptions = {
    onSuccess: () => {
      unitAssessmentState.setHasUnpublishedChanges(true);
      void utils.unitAssessment.getSlideRequirementLinks.invalidate();
    },
    onError: (error: { message: string }) => {
      toast({ title: error.message, status: 'error' });
    },
  };

  const linkMutation = trpcAdmin.unitAssessment.linkSlideRequirement.useMutation(mutationOptions);
  const unlinkMutation =
    trpcAdmin.unitAssessment.unlinkSlideRequirement.useMutation(mutationOptions);

  if (slideId == null || versionId == null || requirements.length === 0 || linksBySlideId == null) {
    return null;
  }

  const linkedIds = new Set(
    (linksBySlideId[slideId] ?? []).map(({ requirementId }) => requirementId),
  );
  const linkedAnywhereIds = new Set(
    Object.values(linksBySlideId).flatMap((links) =>
      links.map(({ requirementId }) => requirementId),
    ),
  );

  const isSkill = ({ kind }: UnitRequirement) => kind === 'FOUNDATION_SKILL';
  const skills = requirements.filter(isSkill);
  const partitioned = requirements.filter((requirement) => !isSkill(requirement));
  const linked = partitioned.filter(({ id }) => linkedIds.has(id));
  const linkedSkills = skills.filter(({ id }) => linkedIds.has(id));

  const groups: RequirementPickerGroup<UnitRequirement>[] = [
    {
      key: 'linked',
      label: <Trans>Linked to this slide</Trans>,
      rows: linked,
      greyed: false,
      linked: true,
    },
    {
      key: 'available',
      label: <Trans>Available</Trans>,
      rows: partitioned.filter(({ id }) => !linkedIds.has(id) && !linkedAnywhereIds.has(id)),
      greyed: false,
      linked: false,
    },
    {
      key: 'elsewhere',
      label: <Trans>Linked to other slides</Trans>,
      rows: partitioned.filter(({ id }) => !linkedIds.has(id) && linkedAnywhereIds.has(id)),
      greyed: false,
      linked: false,
    },
  ];

  const onToggle = (requirement: UnitRequirement, link: boolean) => {
    const input = { slideId, requirementId: requirement.id };

    if (link) {
      linkMutation.mutate(input);
    } else {
      unlinkMutation.mutate(input);
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontSize="sm" fontWeight="medium">
          <Trans>Linked requirements</Trans>
        </Text>

        <RequirementPicker
          groups={groups}
          triggerLabel={<Trans>Link requirements</Trans>}
          triggerIcon={Link2}
          triggerProps={{ 'data-testid': testIds.linkRequirementsButton }}
          optionTestId={testIds.requirementOption}
          idOf={({ id }) => id}
          colourOf={({ kind }) => UNIT_REQUIREMENT_KIND_COLOURS[kind]}
          onToggle={onToggle}
        />
      </Flex>

      <Flex gap={2} wrap="wrap" align="center">
        {linked.map((requirement) => (
          <RequirementChip
            key={requirement.id}
            colourScheme={UNIT_REQUIREMENT_KIND_COLOURS[requirement.kind]}
            label={unitRequirementLabel(requirement)}
            description={requirement.description}
            removeLabel={t`Unlink ${unitRequirementLabel(requirement)}`}
            onRemove={() => onToggle(requirement, false)}
            data-testid={testIds.linkedRequirementChip}
          />
        ))}
      </Flex>

      {skills.length > 0 ? (
        <Box mt={3}>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            <UnitRequirementKindHeading kind="FOUNDATION_SKILL" />
          </Text>

          <FoundationSkillPicker
            options={skills.map(({ code }) => code)}
            selected={linkedSkills.map(({ code }) => code)}
            optionTestId={testIds.foundationSkillOption}
            onToggle={(category, next) => {
              const skill = skills.find(({ code }) => code === category);

              if (skill != null) {
                onToggle(skill, next);
              }
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}
